import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { useAuth } from "../auth/AuthProvider";
import {
  confirmReplaceAccessDevice,
  runCourseAccessCheck,
} from "../utils/accessControl";

export default function CourseAccessGate({
  courseId,
  previewMode = false,
  onAllowed,
  children,
  fallbackLabel = "เริ่มเรียน",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userDoc } = useAuth();

  const [checking, setChecking] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState("");
  const [pendingReplace, setPendingReplace] = useState(null);

  const openGate = async () => {
    if (checking) return;

    if (!previewMode && !user?.uid) {
        navigate("/login", { state: { from: location } });
        return;
    }

    try {
        setChecking(true);
        setBlockedMessage("");

        const result = await runCourseAccessCheck({
        user,
        userDoc,
        courseId,
        previewMode,
        });

        if (result?.status === "allowed" && result?.ok) {
        onAllowed?.(result);
        return;
        }

        if (result?.status === "confirm_replace") {
        setPendingReplace(result);
        return;
        }

        setBlockedMessage(result?.message || "ไม่สามารถเข้าถึงคอร์สนี้ได้");
    } catch (e) {
        setBlockedMessage(e?.message || "ตรวจสอบการเข้าใช้งานไม่สำเร็จ");
    } finally {
        setChecking(false);
    }
    };

  const handleConfirmReplace = async () => {
    try {
        if (!pendingReplace?.device) return;

        setChecking(true);
        setBlockedMessage("");

        const result = await confirmReplaceAccessDevice({
        user,
        userDoc,
        courseId,
        settings: pendingReplace?.settings,
        device: pendingReplace?.device,
        });

        setPendingReplace(null);

        if (result?.status === "allowed" && result?.ok) {
        onAllowed?.(result);
        return;
        }

        setBlockedMessage(result?.message || "ไม่สามารถเปลี่ยนอุปกรณ์ได้");
    } catch (e) {
        setBlockedMessage(e?.message || "ยืนยันเปลี่ยนอุปกรณ์ไม่สำเร็จ");
    } finally {
        setChecking(false);
    }
    };

  return (
    <>
      {typeof children === "function" ? (
        children({ openGate, checking })
      ) : (
        <Button onClick={openGate} disabled={checking}>
          {checking ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              กำลังตรวจสอบ...
            </>
          ) : (
            fallbackLabel
          )}
        </Button>
      )}

      <Modal show={!!blockedMessage} onHide={() => setBlockedMessage("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>ไม่สามารถเข้าเรียนได้</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-0">
            {blockedMessage}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBlockedMessage("")}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!pendingReplace} onHide={() => setPendingReplace(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>ยืนยันเปลี่ยนอุปกรณ์</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            ตรวจพบอุปกรณ์ประเภท <strong>{pendingReplace?.device?.slot}</strong> เครื่องใหม่
          </p>
          <p className="mb-0">
            หากยืนยัน ระบบจะผูกสิทธิ์กับอุปกรณ์นี้ และจะยังไม่สามารถเปลี่ยนอีกได้จนกว่าจะครบกำหนดรอบใหม่
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setPendingReplace(null)}
            disabled={checking}
          >
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={handleConfirmReplace} disabled={checking}>
            {checking ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                กำลังยืนยัน...
              </>
            ) : (
              "ยืนยันเปลี่ยนอุปกรณ์"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}